import React, { useContext, useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import { useDispatch, useSelector } from "react-redux";
import { CanvasContext } from "./CanvasContext";
import { CanvasContext as PreviewCanvasContext } from "./PreviewCanvasContext";
import {
  addCanvasMouseKeyboardEvent,
  generateCanvasImage,
  getActiveObject,
  removeObject,
  sanitizedCanvasObject,
  updateObjectSpecs,
} from "../../canvasUtils";

import {
  setCanvasDimensions,
  setHeight,
  setLayout,
  setWidth,
} from "../../store/editor/canvasSlice";
import ContextMenu from "./ContextMenu";
import { updateCanvasObject } from "../../thunks/editor/canvasThunks";

import "./fabric-canvas.css";
import {
  setIsOnlyAvatar,
  setScaleFactor,
} from "../../store/project/projectSlice";
import { toggleBottomBar } from "../../store/common/uiSlice";
import { useLocation, useParams } from "react-router-dom";
import { LAYOUTS } from "../../constants/general";
import { useCanvasContext } from "./CanvasContext";
// Set default properties for Fabric objects
fabric.Object.prototype.transparentCorners = false;
fabric.Object.prototype.cornerColor = "blue";
fabric.Object.prototype.cornerStyle = "circle";
fabric.Object.prototype.hasControls = true;
// fabric.Object.prototype.cornerSize = 40 * scaleFactor;
fabric.Canvas.prototype.preserveObjectStacking = true;

fabric.CustomVideo = fabric.util.createClass(fabric.Image, {
  type: "customvideo",
  cropRect: null,

  initialize: function (video, options) {
    const defaultOpts = {
      lockRotation: false,
      // objectCaching: true,
      imageSmoothing: true,
      cacheProperties: ["time"],
    };
    options = options || {};

    this.callSuper(
      "initialize",
      video,
      Object.assign({}, defaultOpts, options)
    );
  },
  _draw: function (video, ctx, w, h) {
    const c = this.cropRect;
    const d = {
      x: -this.width / 2,
      y: -this.height / 2,
      w: this.width,
      h: this.height,
    };
    if (c) {
      ctx.drawImage(video, c.x, c.y, c.w, c.h, d.x, d.y, d.w, d.h);
    } else {
      ctx.drawImage(video, d.x, d.y, d.w, d.h);
    }
  },

  _render: function (ctx) {
    this._draw(this.getElement(), ctx);
  },
});

let updateFontTimeout;

let keyDownHandler = () => {};
const FabricCanvas = ({
  previewMode,
  slideTransition,
  initialColor = "white",
}) => {
  const canvasRef = useRef(null);
  // need to rerender fabric canvas manually to render objects.
  // works with strict mode on in local. issue in production.
  const [initialize, setInitialize] = useState();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const w = queryParams.get("width");
  const layout = queryParams.get("layout");
  const r = layout === LAYOUTS.PORTRAIT ? 9 / 16 : 16 / 9;
  // Get the value of the 'height' parameter
  const h = queryParams.get("height");
  const { backgroundColor } = useCanvasContext();

  useEffect(() => {
    if (w && h) {
      dispatch(setHeight(h));
      dispatch(setWidth(w));
      dispatch(setLayout(layout));
    }
  }, [queryParams]);
  let setCanvas, contextCanvas;
  let previewContext = useContext(PreviewCanvasContext);
  let canvasCont = useContext(CanvasContext);
  if (previewMode) {
    setCanvas = previewContext.setCanvas;
    contextCanvas = previewContext.canvas;
  } else {
    setCanvas = canvasCont.setCanvas;
    contextCanvas = canvasCont.canvas;
  }
  const [dimensions, setDimensions] = useState({
    width: canvasCont?.canvas?.getWidth() || 0,
    height: canvasCont?.canvas?.getHeight() || 0,
  });
  const [fontMenu, setFontMenu] = useState({
    show: false,
    position: { x: 0, y: 0, state: "init" },
  });
  const [contextMenu, setContextMenu] = useState({
    show: false,
    position: { x: 0, y: 0, state: "init" },
  });
  // const { width: w, heigh: h, ratio: r } = useSelector((state) => state.canvas);

  const dispatch = useDispatch();
  const { bottomBarExpanded } = useSelector((state) => state.ui);
  const { isOnlyAvatar, avatar, isOnlyAIVoice, isAIdubbingVoice } = useSelector(
    (state) => state.project
  );
  const isonlyavtar = window.location.pathname.includes("only-avatar");

  useEffect(() => {
    if (isonlyavtar) {
      dispatch(setIsOnlyAvatar(true));
    } else {
      dispatch(setIsOnlyAvatar(false));
    }
  }, []);

  const handleOutsideClick = (e) => {
    console.log("clsogin..");
    // Implement logic to check if the click is outside and call onClose
    closeContextMenu();
  };

  React.useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  useEffect(() => {
    const canvasElement = canvasRef.current;
    const canvas = new fabric.Canvas(canvasElement, {
      backgroundColor: initialColor,
    });

    const { height } = handleResize();
    const ratio = r || canvas.getWidth() / canvas.getHeight();
    const scale = height / canvas.getHeight();
    const zoom = canvas.getZoom() * scale;
    const width = height * ratio;

    canvas.setDimensions({
      width: width,
      height: height,
    });
    if (initialize) {
      canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
      if (!!(previewMode && previewMode === true)) {
        canvas.selection = false;
      }
      canvas.upperCanvasEl.addEventListener("contextmenu", (e) =>
        handleContextMenu(canvas, e)
      );
      setCanvas(canvas);
    }

    return () => {
      canvas.dispose();
      setCanvas(null);
    };
  }, [initialize]);

  // rerender the canvas.
  useEffect(() => {
    setInitialize(true);

    return () => {
      dispatch(toggleBottomBar(false));
    };
  }, []);

  const handleContextMenu = (canvas, e) => {
    e.preventDefault();
    if (previewMode) {
      return;
    }
    const nativeEvent = e.nativeEvent || e;
    var pointer = canvas.getPointer(nativeEvent, true);
    var objects = canvas.getObjects();

    for (var i = objects.length - 1; i >= 0; i--) {
      // Considering objects that are not fully transparent and are selectable
      if (
        objects[i].containsPoint(pointer) &&
        objects[i].selectable &&
        objects[i].opacity > 0
      ) {
        canvas.setActiveObject(objects[i]);
        break; // Exit the loop once the topmost object is found and activated
      }
    }
    canvas.renderAll();

    const posX = e.clientX;
    const posY = e.clientY;
    const setAnimation = (val) => {
      let currentObj = canvas.getActiveObject();
      currentObj.animation = val;
      const modifiedObj = sanitizedCanvasObject(currentObj, canvas);
      dispatch(updateCanvasObject({ id: currentObj.id, ...modifiedObj }));
    };
    if (canvas.getActiveObject()) {
      setContextMenu({
        show: true,
        position: { x: posX, y: posY },
        setAnimation: setAnimation,
        selectedAnimation: canvas.getActiveObject()?.animation,
      });
      setFontMenu({ ...fontMenu, position: { x: posX, y: posX } });
    }
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, position: contextMenu.position });
  };
  const closeFontMenu = () => {
    setFontMenu({ show: false });
  };

  const handleTrash = () => {
    const activeObject = contextCanvas.getActiveObject();
    if (activeObject) {
      removeObject(activeObject, contextCanvas, dispatch);
    }
    closeContextMenu();
  };

  const applyFont = (f) => {
    let currentObj = contextCanvas.getActiveObject();
    currentObj.set("fontFamily", f);
    updateObjectSpecs({ target: currentObj }, contextCanvas, dispatch);
    contextCanvas.renderAll();
  };

  const applyColor = (c, type = "color") => {
    let currentObj = contextCanvas.getActiveObject();

    switch (type) {
      case "fontFamily":
        currentObj.set("fontFamily", c);
        break;
      case "fontSize":
        currentObj.set("fontSize", c);
        break;
      case "textAlign":
        currentObj.set("textAlign", c);
        break;
      case "fontStyle":
        // Toggle italic
        currentObj.set(
          "fontStyle",
          currentObj.fontStyle === "italic" ? "normal" : "italic"
        );
        break;
      case "fontWeight":
        currentObj.set("fontWeight", c);
        break;
      case "opacity":
        currentObj.set("opacity", c);
        break;
      case "underline":
        // Toggle underline
        currentObj.set("underline", !currentObj.underline);
        break;
      case "strokeWidth":
        currentObj.set("strokeWidth", c);
        break;
      default:
        if (
          currentObj.type === "graphics" &&
          (currentObj.name || "").toLowerCase().includes("outline")
        ) {
          currentObj.set("stroke", c);
        } else {
          currentObj.set("fill", c);
        }
        break;
    }

    if (updateFontTimeout) {
      clearTimeout(updateFontTimeout);
    }

    updateObjectSpecs({ target: currentObj }, contextCanvas, dispatch);
    contextCanvas.renderAll();
  };

  // Effect for initializing and setting up the Fabric canvas
  useEffect(() => {
    if (contextCanvas && initialize) {
      const ratio = r || contextCanvas.getWidth() / contextCanvas.getHeight();
      const scale = dimensions.height / contextCanvas.getHeight();
      const zoom = contextCanvas.getZoom() * scale;
      const width = dimensions.height * ratio;
      const scaleFactor = w / width;

      dispatch(setScaleFactor(scaleFactor));
      contextCanvas.setDimensions({
        width: width,
        height: dimensions.height,
      });
      contextCanvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);

      window.removeEventListener("keydown", keyDownHandler, true);
      keyDownHandler = (e) => {
        const activeObject = getActiveObject(contextCanvas);

        if (
          activeObject &&
          (activeObject instanceof fabric.IText ||
            activeObject instanceof fabric.Textbox)
        ) {
          return;
        }
        // allow to up/down key to set font-size
        if (e.keyCode === 38 || e.keyCode === 40) {
          return;
        }
        setContextMenu({ show: false, position: { x: 0, y: 0 } });
        closeFontMenu();
        addCanvasMouseKeyboardEvent(e, contextCanvas, dispatch);
      };

      window.addEventListener("keydown", keyDownHandler, true);

      dispatch(setCanvasDimensions(dimensions));
    }

    // Cleanup function
  }, [setCanvas, dispatch, dimensions, previewMode, initialize, contextCanvas]);

  // Effect for handling window resize
  const handleResize = () => {
    if (previewMode) {
      return dimensions;
    }
    let percentage = 0.8;
    if (bottomBarExpanded && !previewMode) {
      percentage = 0.4;
    }
    // if (previewMode) percentage = 0.6;
    if (canvasRef.current) {
      const parentHeight =
        canvasRef.current.parentElement.parentElement.parentElement
          .offsetHeight;
      // const [ratioWidth, ratioHeight] = r.split("/").map(Number);
      // console.log("ratioWidth", ratioWidth, ratioHeight);
      const height = parentHeight * percentage; // 90% of parent width
      const width =
        (height * LAYOUTS.LANDSCAPE ? 16 : 9) / LAYOUTS.LANDSCAPE ? 9 : 16; // 16:9 aspect ratio
      const currentDimensions = { width, height };
      setDimensions(currentDimensions);
      dispatch(setCanvasDimensions(currentDimensions));
      return currentDimensions;
    }
    return dimensions;
  };
  useEffect(() => {
    if (previewMode) {
      return;
    }
    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => window.removeEventListener("resize", handleResize);
  }, [bottomBarExpanded, previewMode]);

  // Effect for updating the Redux store with the canvas image
  useEffect(() => {
    if (contextCanvas && initialize) {
      dispatch(generateCanvasImage(contextCanvas));
    }
  }, [dispatch, initialize]);

  if (isOnlyAIVoice || isAIdubbingVoice) {
    return;
  }

  return (
    <>
      {!isonlyavtar ? (
        <canvas
          className={`canvas-slide ${slideTransition?.name || ""}`}
          ref={canvasRef}
          width={dimensions.width}
          height={dimensions.height}
        />
      ) : (
        <div
          className="flex justify-center border border-grey-400 h-[400px] w-[400px] bg-transparent"
          style={{
            backgroundImage: `url(/tool/dashboard/static/images/BackgroundTransaparentImage.png)`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          {avatar?.url && <img src={avatar.url} />}
        </div>
      )}
      {!previewMode && contextCanvas && initialize && (
        <ContextMenu
          show={contextMenu.show}
          position={contextMenu.position}
          onClose={closeContextMenu}
          type={
            contextCanvas.getActiveObject() &&
            contextCanvas.getActiveObject().type
          }
          onAnimationSelect={(val) => {
            contextMenu.setAnimation(val);
            setContextMenu({ ...contextMenu, selectedAnimation: val });
          }}
          selectedAnimation={contextMenu.selectedAnimation}
          onTrash={handleTrash}
          applyFont={applyFont}
          applyColor={applyColor}
          onFontSettings={() => {
            setFontMenu({ show: true, position: fontMenu.position });
          }}
        />
      )}
    </>
  );
};

export default FabricCanvas;
